import { useState } from 'react';
import { DataFreshness, SaosSyncStatus, SaosSyncResult, adminAPI } from '../../services/admin';
import { useTranslation } from 'react-i18next';

interface DataFreshnessCardProps {
  data: DataFreshness | null;
}

export function DataFreshnessCard({ data }: DataFreshnessCardProps) {
  const [syncStatus, setSyncStatus] = useState<SaosSyncStatus | null>(null);
  const [isCheckingSync, setIsCheckingSync] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<SaosSyncResult | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'pl' ? 'pl-PL' : 'en-US';

  const handleCheckSync = async () => {
    setIsCheckingSync(true);
    setSyncError(null);
    setSyncResult(null);
    try {
      const status = await adminAPI.getSaosSyncStatus();
      setSyncStatus(status);
    } catch (err) {
      setSyncError(err instanceof Error ? err.message : t('dataFreshnessCard.checkError'));
    } finally {
      setIsCheckingSync(false);
    }
  };

  const handleSync = async () => {
    if (!confirm(t('dataFreshnessCard.confirmSync'))) {
      return;
    }

    setIsSyncing(true);
    setSyncError(null);
    setSyncResult(null);
    try {
      const result = await adminAPI.triggerSaosSync();
      setSyncResult(result);
      // Refresh sync status after successful sync
      const status = await adminAPI.getSaosSyncStatus();
      setSyncStatus(status);
    } catch (err) {
      setSyncError(err instanceof Error ? err.message : t('dataFreshnessCard.syncError'));
    } finally {
      setIsSyncing(false);
    }
  };

  if (!data) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">{t('dataFreshnessCard.title')}</h2>
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-3/4" />
          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/2" />
        </div>
      </div>
    );
  }

  const formatLastSync = (lastSync: string | null, daysSince: number | null): string => {
    if (!lastSync) return t('dataFreshnessCard.never');
    if (daysSince === 0) return t('dataFreshnessCard.today');
    if (daysSince === 1) return t('dataFreshnessCard.yesterday');
    if (daysSince !== null) return t('dataFreshnessCard.daysAgo', { count: daysSince });
    return new Date(lastSync).toLocaleDateString(locale);
  };

  const getFreshnessColor = (daysSince: number | null): string => {
    if (daysSince === null) return 'text-slate-500';
    if (daysSince <= 1) return 'text-green-600 dark:text-green-400';
    if (daysSince <= 7) return 'text-amber-600 dark:text-amber-400';
    return 'text-red-600 dark:text-red-400';
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
      <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">{t('dataFreshnessCard.title')}</h2>

      <div className="space-y-4">
        {/* SAOS */}
        <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium text-slate-900 dark:text-white">SAOS</span>
            <span className={`text-sm font-medium ${getFreshnessColor(data.saos.daysSinceSync)}`}>
              {formatLastSync(data.saos.lastSync, data.saos.daysSinceSync)}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-500 dark:text-slate-400">{t('dataFreshnessCard.judgmentsInDb')}</span>
            <span className="text-slate-700 dark:text-slate-300">
              {data.saos.totalJudgments.toLocaleString()}
            </span>
          </div>

          {/* Sync Status */}
          {syncStatus && (
            <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-600">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-500 dark:text-slate-400">{t('dataFreshnessCard.inSaos')}</span>
                <span className="text-slate-700 dark:text-slate-300">
                  {syncStatus.saos.total.toLocaleString()}
                </span>
              </div>
              {syncStatus.comparison.needsSync ? (
                <div className="flex items-center gap-2 mt-2">
                  <span className="w-2 h-2 bg-amber-500 rounded-full" />
                  <span className="text-sm text-amber-600 dark:text-amber-400">
                    {t('dataFreshnessCard.missingJudgments', { count: syncStatus.comparison.difference })}
                    ({(100 - syncStatus.comparison.percentSynced).toFixed(2)}%)
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2 mt-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full" />
                  <span className="text-sm text-green-600 dark:text-green-400">
                    {t('dataFreshnessCard.synced')}
                  </span>
                </div>
              )}
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                {t('dataFreshnessCard.checkedAt', { date: new Date(syncStatus.checkedAt).toLocaleString(locale) })}
              </p>
            </div>
          )}

          {syncError && (
            <p className="text-xs text-red-500 mt-2">{syncError}</p>
          )}

          {/* Sync Result */}
          {syncResult && (
            <div className="mt-3 p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <p className="text-xs text-green-700 dark:text-green-300 font-medium">
                {syncResult.message}
              </p>
              <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                Pobrano: {syncResult.fetched.toLocaleString()} orzeczeń
                ({syncResult.startDate} - {syncResult.endDate})
              </p>
            </div>
          )}

          {/* Buttons */}
          <div className="mt-3 flex gap-2">
            <button
              onClick={handleCheckSync}
              disabled={isCheckingSync || isSyncing}
              className="flex-1 px-3 py-1.5 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 disabled:opacity-50 transition-colors"
            >
              {isCheckingSync ? t('dataFreshnessCard.checking') : t('dataFreshnessCard.check')}
            </button>
            {syncStatus?.comparison.needsSync && (
              <button
                onClick={handleSync}
                disabled={isSyncing || isCheckingSync}
                className="flex-1 px-3 py-1.5 text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/50 disabled:opacity-50 transition-colors flex items-center justify-center gap-1"
              >
                {isSyncing && (
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-green-600" />
                )}
                {isSyncing ? t('dataFreshnessCard.syncing') : t('dataFreshnessCard.sync')}
              </button>
            )}
          </div>
        </div>

        {/* CBOSA */}
        <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="font-medium text-slate-900 dark:text-white">CBOSA (NSA)</span>
              {data.cbosa.isRunning && (
                <span className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400">
                  <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
                  {t('dataFreshnessCard.inProgress')}
                </span>
              )}
            </div>
            <span className={`text-sm font-medium ${getFreshnessColor(data.cbosa.daysSinceSync)}`}>
              {data.cbosa.isRunning ? t('dataFreshnessCard.downloading') : formatLastSync(data.cbosa.lastSync, data.cbosa.daysSinceSync)}
            </span>
          </div>

          {/* Progress when running */}
          {data.cbosa.isRunning && data.cbosa.progress && (
            <div className="mb-2">
              <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400 mb-1">
                <span>
                  {data.cbosa.progress.current.toLocaleString()} / {data.cbosa.progress.total.toLocaleString()}
                </span>
                <span>{data.cbosa.progress.percent.toFixed(2)}%</span>
              </div>
              <div className="h-1.5 bg-slate-200 dark:bg-slate-600 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 rounded-full transition-all duration-300"
                  style={{ width: `${Math.min(data.cbosa.progress.percent, 100)}%` }}
                />
              </div>
              {data.cbosa.progress.message && (
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  {data.cbosa.progress.message}
                </p>
              )}
            </div>
          )}

          <div className="flex justify-between text-sm">
            <span className="text-slate-500 dark:text-slate-400">{t('dataFreshnessCard.judgmentsInDb')}</span>
            <span className="text-slate-700 dark:text-slate-300">
              {data.cbosa.totalJudgments.toLocaleString()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
